import { keyBy } from 'lodash';
import { NormalizedSchema, denormalize, normalize } from 'normalizr';
import type { Team } from 'api/models';
import {
  getApiFilterTransformer,
  to_filter,
} from 'components/pages/api-filter-transformer';
import type {
  DynamicTemplateConditions,
  ProjectTemplate,
  ProjectTemplateApi,
  ProjectTemplateProjectPhase,
  ProjectTemplateSpace,
  ProjectTemplateStoreRecord,
  ProjectTemplateTask,
  TemplateAllocation,
  AllocatedMembers,
  TemplateContent,
} from '../types';
import { getDynamicConditionFields } from '../utils';
import { templateSchema } from './schema';

export interface NormalizedProjectTemplatePayload {
  template: ProjectTemplate;
  projectPhases: {
    [key: string]: ProjectTemplateProjectPhase;
  };
  tasks: {
    [key: string]: ProjectTemplateTask;
  };
  spaceList: {
    [key: string]: ProjectTemplateSpace;
  };
  dynamicConditions: DynamicTemplateConditions;
  allocations: {
    [key: number]: TemplateAllocation;
  };
  allocatedMembers: AllocatedMembers;
  placeholders: {
    [key: number]: Team;
  };
}
export const normalizeTemplatePayload = (
  payload: ProjectTemplateApi,
): NormalizedSchema<NormalizedProjectTemplatePayload, string> =>
  normalize(payload, templateSchema);

export const denormalizePayload = (
  payload: NormalizedProjectTemplatePayload,
): ProjectTemplateApi => {
  const denormalizedData = denormalize(
    { template: payload.template },
    { template: templateSchema },
    payload,
  );

  return denormalizedData.template;
};

export const getDynamicConditionsFromPayload = (
  templateContent: TemplateContent<'api'>,
  projectCustomFields,
): DynamicTemplateConditions => {
  const conditions: DynamicTemplateConditions = {};

  const fields = getDynamicConditionFields(projectCustomFields);
  const transformer = getApiFilterTransformer(keyBy(fields, 'fieldName'));

  templateContent.projectPhases.forEach((phase) => {
    if (phase.filters) {
      conditions[phase.projectPhaseId] = transformer.from(phase.filters);
    }
    phase.tasks.forEach((task) => {
      if (task.filters) {
        conditions[task.taskId] = transformer.from(task.filters);
      }
      task.subTasks.forEach((subTask) => {
        if (subTask.filters) {
          conditions[subTask.taskId] = transformer.from(subTask.filters);
        }
        subTask.subTasks.forEach((_subTask) => {
          if (_subTask.filters) {
            conditions[_subTask.taskId] = transformer.from(_subTask.filters);
          }
        });
      });
    });
  });

  templateContent.spaceList.forEach((space) => {
    if (space.filters) {
      conditions[space.spaceId] = transformer.from(space.filters);
    }
    space.spaceTabs.forEach((spaceTab) => {
      if (spaceTab.filters) {
        conditions[spaceTab.spaceTabId] = transformer.from(spaceTab.filters);
      }
    });
  });

  return conditions;
};

export const setDynamicConditionInPayload = (
  projectTemplate: NormalizedProjectTemplatePayload,
): NormalizedProjectTemplatePayload => {
  const { projectPhases, tasks, spaceList, dynamicConditions } =
    projectTemplate;

  const _phases = Object.values(projectPhases).map((_phase) => {
    const { projectPhaseId, filters, ...rest } = _phase;

    if (dynamicConditions && dynamicConditions[projectPhaseId]) {
      return {
        ..._phase,
        filters: to_filter(dynamicConditions[projectPhaseId]),
      };
    }

    return { projectPhaseId, ...rest };
  });

  const _tasks = Object.values(tasks).map((_task) => {
    const { taskId, filters, ...rest } = _task;

    if (dynamicConditions && dynamicConditions[taskId]) {
      return { ..._task, filters: to_filter(dynamicConditions[taskId]) };
    }

    return { taskId, ...rest } as ProjectTemplateTask;
  });

  const _spaceLists = Object.values(spaceList).map((_spaceList) => {
    const { spaceId, filters, ...rest } = _spaceList;

    const newSpaceList = { spaceId, ...rest };

    newSpaceList.spaceTabs = _spaceList.spaceTabs.map((_spaceTab) => {
      const { spaceTabId, filters: spaceTabFilters, ...restdata } = _spaceTab;

      if (dynamicConditions && dynamicConditions[spaceTabId]) {
        return {
          ..._spaceTab,
          filters: to_filter(dynamicConditions[spaceTabId]),
        };
      }

      return { spaceTabId, ...restdata };
    });

    if (dynamicConditions && dynamicConditions[spaceId]) {
      return {
        ...newSpaceList,
        filters: to_filter(dynamicConditions[spaceId]),
      };
    }

    return newSpaceList;
  });

  return {
    ...projectTemplate,
    projectPhases: keyBy(_phases, 'projectPhaseId'),
    tasks: keyBy(_tasks, 'taskId'),
    spaceList: keyBy(_spaceLists, 'spaceId'),
  };
};

export const validatePhaseShiftInPayload = (
  projectTemplate: NormalizedProjectTemplatePayload,
): NormalizedProjectTemplatePayload => {
  const { projectPhases } = projectTemplate;

  const sortedPhases = Object.values(projectPhases).sort(
    (phase1, phase2) => phase1.startDay - phase2.startDay,
  );

  const _phases = sortedPhases.map((_phase, index) => {
    const { startDay, shiftDays } = _phase;

    if (shiftDays && shiftDays > 0) {
      const nextPhase = sortedPhases[index + 1];
      const nextStartDay = nextPhase?.startDay ?? 0;

      const maxShiftDays = Math.max(0, nextStartDay - startDay);

      if (shiftDays > maxShiftDays) {
        return { ..._phase, shiftDays: maxShiftDays };
      }
    }

    return _phase;
  });

  return {
    ...projectTemplate,
    projectPhases: keyBy(_phases, 'projectPhaseId'),
  };
};

export const getDynamicFieldsForPreview = (
  projectTemplate: ProjectTemplateStoreRecord,
) => {
  const { dynamicConditions } = projectTemplate;
  const fields = {};

  if (dynamicConditions) {
    Object.values(dynamicConditions).forEach(({ criteria }) => {
      criteria.forEach(({ field }) => {
        const { fieldId, fieldName } = field;

        fields[fieldId < 0 ? fieldName : fieldId] = field;
      });
    });
  }

  return fields;
};
